import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';

import { Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import { IntervalFormatter, IntervalValidation } from '../../utils';
import { IntervalBar } from '../charts';
import { ConsumptionRangesTable } from '../tables';
import { ConsumptionRangeForm } from '../forms/ConsumptionRangeForm';
import { CleanButton, PrimaryButton } from '../default';

export const ScoreRangeRulesModal = ({ open, onClose, onSave, scoreRange, scoreRangeIndex }) => {

    const [creditLimitMaxValue, setCreditLimitMaxValue] = useState(null)
    const [creditLimitMaxValueError, setCreditLimitMaxValueError] = useState({ state: false, message: '' })

    const [creditLimitMinValue, setCreditLimitMinValue] = useState(null)
    const [creditLimitMinValueError, setCreditLimitMinValueError] = useState({ state: false, message: '' })

    const [intervalList, setIntervalList] = useState([])
    const [intervalError, setIntervalError] = useState({ state: false, message: '' })

    useEffect(() => {

        if (open && scoreRange && Array.isArray(scoreRange.consumptionRanges)) {

            setCreditLimitMaxValue(scoreRange.creditLimitMaxValue)
            setCreditLimitMaxValueError({ state: false, message: '' })

            setCreditLimitMinValue(scoreRange.creditLimitMinValue)
            setCreditLimitMinValueError({ state: false, message: '' })

            const formattedRanges = IntervalFormatter.backToFrontIntervalFormatter(scoreRange.consumptionRanges)

            setIntervalList(formattedRanges)
            setIntervalError({ state: false, message: '' })

        } else if (open) {
            setCreditLimitMaxValue(null)
            setCreditLimitMaxValueError({ state: false, message: '' })

            setCreditLimitMinValue(null)
            setCreditLimitMinValueError({ state: false, message: '' })

            setIntervalList([])
            setIntervalError({ state: false, message: '' })
        }

        // eslint-disable-next-line
    }, [open, scoreRange?.scoreRangeId])

    const handleRangeDelete = id => {
        setIntervalList(prev => prev.filter(i => i.consumptionRangeId !== id))
    }

    const submitRules = () => {

        if (creditLimitMaxValue !== null && creditLimitMaxValue < 1) {
            setCreditLimitMaxValueError({
                state: true,
                message: 'Insira um valor válido (maior ou igual a R$ 1,00)! Deixe em branco se não quiser definir um valor máximo.'
            })
            return
        }

        if (creditLimitMinValue !== null && creditLimitMinValue < 0) {
            setCreditLimitMinValueError({
                state: true,
                message: 'Insira um valor válido (maior ou igual a R$ 0,00)! Deixe em branco se não quiser definir um valor mínimo.'
            })
            return
        }

        if (!IntervalValidation.validateIntervalDist(intervalList)) {
            setIntervalError({
                state: true,
                message: 'Defina as regras para todo o intervalo de consumo (0-100%).'
            })
            return
        }

        const postFormattedIntervalList = IntervalFormatter.postIntervalFormatter(intervalList)

        onSave(creditLimitMinValue, creditLimitMaxValue, postFormattedIntervalList)
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: { p: 2, borderRadius: 2 }
            }}>

            <DialogTitle>
                <Stack direction="row" alignItems="center" gap={1}>

                    <Chip sx={{ fontWeight: 600, fontSize: 14 }} label={`${scoreRangeIndex + 1}ª`} variant='outlined' color='primary' />

                    <Typography variant="h6">
                        {`${scoreRange?.minScore ?? 0} - ${scoreRange?.maxScore ?? ''}`}
                    </Typography>

                </Stack>

                <Divider width="100%" sx={{ mt: 1 }} />
            </DialogTitle>

            <DialogContent>

                <Stack gap={5}>

                    <Stack direction='row' gap={3}>
                        <Stack gap={3}>
                            <Stack>
                                <Typography fontWeight="bold">Limite Mínimo de Crédito</Typography>
                                <Typography variant="body2">
                                    Defina o limite mínimo de crédito que os clientes nesta faixa de score podem receber. Deixe em branco se não quiser definir um valor mínimo.
                                </Typography>
                            </Stack>
                            <NumericFormat
                                value={creditLimitMinValue ?? ''}
                                onValueChange={(values) => setCreditLimitMinValue(values.value === '' ? null : values.floatValue)}

                                thousandSeparator={'.'}
                                decimalSeparator={','}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                allowNegative={false}
                                customInput={TextField}

                                label="Limite Mínimo de Crédito"
                                error={creditLimitMinValueError.state}
                                helperText={creditLimitMinValueError.message}
                                onFocus={(event) => event.target.select()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            R$
                                        </InputAdornment>
                                    )
                                }}
                                fullWidth
                            />
                        </Stack>

                        <Stack gap={3}>
                            <Stack>
                                <Typography fontWeight="bold">Limite Máximo de Crédito</Typography>
                                <Typography variant="body2">
                                    Defina o limite máximo de crédito que os clientes nesta faixa de score podem receber. Deixe em branco se não quiser definir um valor máximo.
                                </Typography>
                            </Stack>
                            <NumericFormat
                                value={creditLimitMaxValue ?? ''}
                                onValueChange={(values) => setCreditLimitMaxValue(values.value === '' ? null : values.floatValue)}

                                thousandSeparator={'.'}
                                decimalSeparator={','}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                allowNegative={false}
                                customInput={TextField}

                                label="Limite Máximo de Crédito"
                                error={creditLimitMaxValueError.state}
                                helperText={creditLimitMaxValueError.message}
                                onFocus={(event) => event.target.select()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            R$
                                        </InputAdornment>
                                    )
                                }}
                                fullWidth
                            />
                        </Stack>
                    </Stack>

                    <ConsumptionRangeForm
                        intervalList={intervalList} setIntervalList={setIntervalList}
                        intervalError={intervalError} setIntervalError={setIntervalError}
                    />

                    <IntervalBar intervalList={intervalList} />

                    <ConsumptionRangesTable intervalList={intervalList} onDelete={handleRangeDelete} />

                </Stack>

            </DialogContent>

            <DialogActions>
                <CleanButton
                    label='Cancelar'
                    onClick={onClose}
                />
                <PrimaryButton
                    label='Salvar Regras'
                    onClick={submitRules}
                    disabled={intervalList.length === 0}
                />
            </DialogActions>
        </Dialog>
    )
}

ScoreRangeRulesModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    scoreRange: PropTypes.shape({
        scoreRangeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        minScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        maxScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        consumptionRanges: PropTypes.array,
    }),
    scoreRangeIndex: PropTypes.number.isRequired
}
